// Drawing a pod's route as a commit graph.
//
// Shared by the two sheets that show one: what a pod is waiting on, and its
// whole journey. They differ only in which nodes they contain — the reading is
// the same either way, so the drawing is one piece of code: the earliest work
// at the top, time running down the screen, branches in their own lanes.

import { useEffect, useRef, type CSSProperties } from 'react';
import type { JourneyStepVM, WoState } from '@/api/types';
import { stateLabel } from '@/components/pod/PodParts';
import { FOOT_ROW, type UpstreamGraph } from '@/lib/upstreamGraph';
import { useT } from '@/i18n/useT';

/** Row geometry. */
const ROW_H = 62;
const FOOT_H = 34;
const LANE_W = 22;
const DOT = 9;

/**
 * How much of the width the lanes may take before they start squeezing.
 *
 * A real route branches harder than a diagram suggests — one pod's end-of-line
 * QC waits on eight separate threads, so eleven lanes are live at once — and at
 * a full 22px each that left the work centre names clipped to two letters. The
 * lanes narrow instead: the graph is there to be read alongside the names, not
 * instead of them.
 */
const MAX_GUTTER = 118;

/** Lane spacing for a graph this wide, and the gutter it needs. */
export function laneWidth(lanes: number): number {
  return Math.max(11, Math.min(LANE_W, Math.round(MAX_GUTTER / Math.max(1, lanes))));
}

/**
 * How each state reads as a marker.
 *
 * The four an operator has to tell apart at a glance — waiting, ready, running,
 * finished — were three shades of the same dot. Now the mark carries the state:
 * filled where the work has a result, ringed where it is merely available, and
 * grown so the live rows sit proudest on the rail. `tone` colours the state
 * word on the right of the row to match, so the two agree.
 */
const MARK: Record<WoState, { fill: string; ring?: string; grow: number; tone: string }> = {
  progress: { fill: 'var(--accent)', grow: 4, tone: 'var(--accent)' },
  ready: { fill: 'var(--bg)', ring: 'var(--accent)', grow: 3, tone: 'var(--accent)' },
  waiting: { fill: 'var(--bg)', ring: 'var(--warn)', grow: 0, tone: 'var(--warn)' },
  pending: { fill: 'var(--surface2)', ring: 'var(--line)', grow: 0, tone: 'var(--sub)' },
  done: { fill: 'var(--pos)', grow: 1, tone: 'var(--pos)' },
  cancel: { fill: 'var(--line)', grow: 0, tone: 'var(--sub)' },
};

const isSettled = (s: JourneyStepVM) => s.state === 'done' || s.state === 'cancel';

export function GraphRows({
  graph,
  foot,
  hereWoId,
  onJumpTo,
}: {
  graph: UpstreamGraph;
  /**
   * A station to draw below the graph, which every row nothing else waits on
   * connects down to. The waiting-on sheet uses it for the operator's own
   * station: its blockers stack above it rather than hang off each other.
   */
  foot?: string;
  /** The operator's own step, marked in place rather than as a separate row. */
  hereWoId?: number;
  /** Go to that work centre with this pod selected. */
  onJumpTo?: (step: JourneyStepVM) => void;
}) {
  const laneW = laneWidth(graph.lanes);
  const gutter = graph.lanes * laneW;
  const footH = foot ? FOOT_H : 0;
  const hereRef = useRef<HTMLDivElement>(null);

  // A full route runs off the bottom of the screen, and the row the operator
  // cares about is somewhere in the middle of it. Put it in view rather than
  // making them scroll to find themselves.
  useEffect(() => {
    hereRef.current?.scrollIntoView({ block: 'center' });
  }, [hereWoId]);

  return (
    <div style={{ position: 'relative' }}>
      <Rails graph={graph} gutter={gutter} laneW={laneW} footH={footH} />
      {graph.nodes.map((node) => {
        const here = node.step.woId === hereWoId;
        return (
          <div key={node.step.woId} {...(here ? { ref: hereRef } : {})}>
            <Row
              step={node.step}
              lane={node.lane}
              laneW={laneW}
              gutter={gutter}
              here={here}
              // Finished work is not somewhere to be sent, and neither is the
              // station the operator is already standing at.
              {...(onJumpTo && !here && !isSettled(node.step)
                ? { onJump: () => onJumpTo(node.step) }
                : {})}
            />
          </div>
        );
      })}
      {foot && <YouRow gutter={gutter} laneW={laneW} label={foot} />}
    </div>
  );
}

/** The operator's own station, drawn as the foot of a graph that excludes it. */
function YouRow({ gutter, laneW, label }: { gutter: number; laneW: number; label: string }) {
  const t = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: FOOT_H, position: 'relative' }}>
      <span style={{ width: gutter, flex: 'none', position: 'relative' }}>
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: laneW / 2,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: 'var(--sub)',
            boxShadow: '0 0 0 3px var(--bg)',
          }}
        />
      </span>
      <span style={{ font: '600 11px Manrope,sans-serif', color: 'var(--sub)' }}>
        {label} · {t.waitingOnYouAreHere}
      </span>
    </div>
  );
}

/**
 * The lines. Drawn as one SVG behind the rows so an edge can cross lanes
 * without fighting the rows' own layout.
 */
function Rails({
  graph,
  gutter,
  laneW,
  footH,
}: {
  graph: UpstreamGraph;
  gutter: number;
  laneW: number;
  footH: number;
}) {
  const height = graph.nodes.length * ROW_H + footH;
  const x = (lane: number) => lane * laneW + laneW / 2;
  // The foot row is half as tall as the rest, so its marker is not on the row
  // grid; everything above it is.
  const y = (row: number) =>
    row === FOOT_ROW ? graph.nodes.length * ROW_H + footH / 2 : row * ROW_H + ROW_H / 2;

  // Every line runs from a row up to one above it: a node to the blockers it
  // waits on, and the foot to whatever the graph ends on.
  const lines: Array<{ from: number; fromY: number; to: number; toY: number }> = [];
  graph.nodes.forEach((node, i) => {
    for (const edge of node.edgesUp) {
      lines.push({ from: x(node.lane), fromY: y(i), to: x(edge.lane), toY: y(edge.row) });
    }
  });
  if (footH) {
    // The foot sits in the trunk lane, so its lines converge there the way a
    // merge does — the operator's station is what all of this feeds into.
    for (const edge of graph.footEdges) {
      lines.push({ from: x(0), fromY: y(FOOT_ROW), to: x(edge.lane), toY: y(edge.row) });
    }
  }

  return (
    <svg
      aria-hidden="true"
      width={gutter}
      height={height}
      style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}
    >
      {lines.map((line, i) => {
        // The line runs up in its own lane and bends into the target's lane in
        // the row below it — the way a branch merges in a commit graph. Drawing
        // every edge one row tall, as this used to, turned a merge of eight
        // flowline steps into eight stubs pointing at nothing.
        const bend = Math.min(line.fromY, line.toY + ROW_H);
        const d =
          line.from === line.to
            ? `M ${line.from} ${line.fromY} L ${line.to} ${line.toY}`
            : `M ${line.from} ${line.fromY} L ${line.from} ${bend} C ${line.from} ${(bend + line.toY) / 2}, ${line.to} ${(bend + line.toY) / 2}, ${line.to} ${line.toY}`;
        return (
          <path
            key={i}
            d={d}
            fill="none"
            stroke="var(--line)"
            strokeWidth={2}
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}

function Row({
  step,
  lane,
  laneW,
  gutter,
  here = false,
  onJump,
}: {
  step: JourneyStepVM;
  lane: number;
  laneW: number;
  gutter: number;
  here?: boolean;
  onJump?: () => void;
}) {
  const t = useT();
  const settled = isSettled(step);
  const mark = MARK[step.state] ?? MARK.pending;
  // Markers shrink with the lanes, so neighbouring branches stay distinct
  // rather than merging into a smear of dots.
  const size = (laneW < 16 ? DOT - 2 : DOT) + mark.grow;
  const ringW = size >= 12 ? 3 : 2;
  const punch = size >= 12 ? 4 : 3;

  const body = (
    <>
      <span style={{ width: gutter, flex: 'none', position: 'relative', alignSelf: 'stretch' }}>
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: lane * laneW + laneW / 2,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: size,
            height: size,
            borderRadius: '50%',
            background: mark.fill,
            border: mark.ring ? `${ringW}px solid ${mark.ring}` : 'none',
            // A ring in the page colour punches the dot out of the rail behind
            // it, so the line does not run through the middle of the marker.
            // The operator's own row wears a second, accent ring instead.
            boxShadow: here
              ? `0 0 0 ${punch - 1}px var(--bg), 0 0 0 ${punch + 1}px var(--accent)`
              : `0 0 0 ${punch}px var(--bg)`,
            animation: step.state === 'progress' ? 'ipPulse 2.4s infinite' : 'none',
          }}
        />
      </span>

      {/* Clear of the lanes: the outermost marker sits at the very edge of the
          gutter, and without this the text starts underneath it. */}
      <span style={{ flex: 1, minWidth: 0, textAlign: 'left', paddingLeft: 8 }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span
            style={{
              font: "600 13px 'Space Grotesk',sans-serif",
              color: settled && !here ? 'var(--sub)' : 'var(--ink)',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {step.workcentreName || step.name}
          </span>
          <span
            style={{
              marginLeft: 'auto',
              flex: 'none',
              font: '600 10px Manrope,sans-serif',
              color: here ? 'var(--accent)' : mark.tone,
            }}
          >
            {here ? t.waitingOnYouAreHere : stateLabel(step.state)}
          </span>
        </span>
        {/* Most work orders are named after their work centre; printing both
            just says it twice. */}
        {step.name && step.name !== step.workcentreName && (
          <span
            style={{
              display: 'block',
              font: '500 11px Manrope,sans-serif',
              color: 'var(--sub)',
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {step.name}
          </span>
        )}
      </span>

      {onJump && (
        <span aria-hidden="true" style={{ flex: 'none', color: 'var(--sub)', paddingLeft: 6 }}>
          ›
        </span>
      )}
    </>
  );

  const style: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: ROW_H,
    position: 'relative',
  };

  return onJump ? (
    <button type="button" className="press-row" onClick={onJump} style={style}>
      {body}
    </button>
  ) : (
    <div style={style} aria-current={here ? 'step' : undefined}>
      {body}
    </div>
  );
}
